import { useQuery, useMutation } from "@tanstack/react-query";
import api from "../axios-client";
import { queryClient } from "../query-client";

export type DashboardWidget = {
    i: string;
    type: "metric" | "chart" | "map" | "status" | "header" | "valueGroup";
    layouts: Record<string, { x: number; y: number; w: number; h: number }>;
    attributeKey?: string | null;
    title?: string | null;
    options?: Record<string, unknown> | null;
};

export type Dashboard = {
    id: string;
    modelId: number;
    name: string;
    isDefault: boolean;
    widgets: DashboardWidget[];
    model?: { id: string; name: string };
};

export const useGetDashboards = (modelId?: string) =>
    useQuery<Dashboard[]>({
        queryKey: ["dashboards", modelId],
        queryFn: async () => {
            const response = await api.get("/api/v1/dashboards", {
                params: modelId ? { modelId } : undefined,
            });
            return response.data.data;
        },
        retry: false,
        enabled: !!modelId,
    });

export const useGetDashboard = (id?: string) =>
    useQuery<Dashboard>({
        queryKey: ["dashboard", id],
        queryFn: async () => {
            const response = await api.get(`/api/v1/dashboards/${id}`);
            return response.data.data;
        },
        retry: false,
        enabled: !!id,
    });

export const useCreateDashboard = () =>
    useMutation({
        mutationFn: async (data: { modelId: string; name: string; widgets?: DashboardWidget[]; isDefault?: boolean }) => {
            const response = await api.post("/api/v1/dashboards", data);
            return response.data.data;
        },
        onSuccess: () => {
            queryClient.refetchQueries({ queryKey: ["dashboards"] });
        },
    });

export const useUpdateDashboard = () =>
    useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<{ name: string; widgets: DashboardWidget[]; isDefault: boolean }> }) => {
            const response = await api.put(`/api/v1/dashboards/${id}`, data);
            return response.data.data;
        },
        onSuccess: (_data, variables) => {
            queryClient.refetchQueries({ queryKey: ["dashboards"] });
            queryClient.refetchQueries({ queryKey: ["dashboard", variables.id] });
        },
    });

export const useSetDefaultDashboard = () =>
    useMutation({
        mutationFn: async (id: string) => {
            const response = await api.post(`/api/v1/dashboards/${id}/set-default`);
            return response.data.data;
        },
        onSuccess: () => {
            queryClient.refetchQueries({ queryKey: ["dashboards"] });
        },
    });

export const useDeleteDashboard = () =>
    useMutation({
        mutationFn: async (id: string) => {
            const response = await api.delete(`/api/v1/dashboards/${id}`);
            return response.data.data;
        },
        onSuccess: () => {
            queryClient.refetchQueries({ queryKey: ["dashboards"] });
        },
    });
